import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'
import DavidStyledText from '../DavidStyledText'
import { PaintBrushStroke } from '../PaintBrushStroke'
import { SpecificTimesToolTip } from '../ToolTips/SpecificTimesToolTip'

interface Props {
  className?: string
}

const code = 'KIDSGOFREE'
const backgroundImage = { url: '/v1761058177/david/kids-go-free-banner-bg', width: 4240, height: 2560 }
const foregroundImage = { url: '/v1761058174/david/david-kidsgofree-bg-2_2', width: 1620, height: 560 }
const accentColor = '#EA6F13'

const DavidKidsGoFreeBanner: React.FC<Props> = ({ className }) => {
  const { t } = useTranslate('tickets')
  const { experiments } = useTheatricalPromotionContext()

  const useAltCopy = experiments?.['david-kidsgofree-promo'] ?? false

  const leftContent = useMemo(() => {
    if (useAltCopy) {
      return (
        <DavidStyledText
          className="whitespace-pre-line text-[40px] @md:text-[58px] @xl:text-[74px] @3xl:text-[50px] @4xl:text-[58px] @5xl:text-[66px] @6xl:text-[72px] @7xl:text-[90px]"
          face="royal-loudes-rough"
        >
          {t('freeTicketsForKids', 'free tickets\nfor kids')}
        </DavidStyledText>
      )
    }

    return (
      <>
        <DavidStyledText
          className="whitespace-nowrap text-center text-[18px] @sm:text-[22px] @lg:text-[28px]"
          face="royal-loudes-rough"
        >
          {t('joinTheMovementToHelp', 'join the movement to help')}
        </DavidStyledText>
        <DavidStyledText
          className="whitespace-nowrap text-center text-[58px] @sm:text-[74px] @lg:text-[90px]"
          face="royal-loudes-rough"
        >
          {t('oneMillion', '1 million')}
        </DavidStyledText>
        <DavidStyledText className="whitespace-nowrap text-center text-[18px] @sm:text-[22px] @lg:text-[28px]">
          {t('kidsGoFreeOnDec19', 'kids go free on dec 19')}
        </DavidStyledText>
      </>
    )
  }, [useAltCopy, t])

  return (
    <div className={classNames(className)}>
      <div className="relative z-[39] h-[140px] rounded-xl @container">
        <div className="absolute h-[140px] w-full overflow-hidden rounded-xl">
          <div className="absolute inset-0 z-0" />
          {/* Background image */}
          <Image
            src={backgroundImage.url}
            className="z-10"
            fill={true}
            style={{ objectFit: 'cover', objectPosition: '50% 50%' }}
            alt="Kids Go Free Background"
            quality={50}
            sizes="(min-width: 1500px) 1500px, 100vw"
          />
          {/* Foreground image */}
          <Image
            src={foregroundImage.url}
            className="absolute right-0 top-0 z-[11] h-[140px] rounded-r-xl opacity-20 @3xl:opacity-100"
            height={140}
            width={(foregroundImage.width / foregroundImage.height) * 140}
            alt="Kids Go Free Foreground"
          />
        </div>
        <div className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-between px-3 @lg:justify-around @3xl:justify-between @3xl:pl-6 @3xl:pr-[50%]">
          <div className="flex flex-col flex-nowrap justify-center">{leftContent}</div>

          <div className="flex flex-col flex-nowrap items-start justify-center @3xl:translate-x-[50%]">
            <div className="flex flex-col items-center text-center">
              <DavidStyledText className="text-[18px] @sm:text-[22px] @lg:text-[28px]">
                {t('useCodeLowerCase', 'use code')}
              </DavidStyledText>
              <PaintBrushStroke color={accentColor}>
                <DavidStyledText
                  color="white"
                  className="block px-4 py-3 text-[28px] lowercase @sm:text-[32px] @md:px-8 @lg:text-[40px]"
                >
                  {code}
                </DavidStyledText>
              </PaintBrushStroke>

              <DavidStyledText className="hidden text-[12px] @sm:block @lg:text-[16px]">
                {t('whileSuppliesLastOnePerAdultTicket', 'while supplies last, 1 per adult ticket')}
              </DavidStyledText>

              <SpecificTimesToolTip
                textColor={'black'}
                buttonColor={'core-gray-800'}
                buttonClassName="flex items-center focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent whitespace-nowrap @sm:mt-1"
                buttonStyle={{ padding: 0 }}
                copy={[t('validForShowtimesFromDec18ToDec21', 'Valid for Showtimes from 12/18 to 12/21')]}
              >
                <DavidStyledText className="mt-[1px] text-[16px]" color="core-gray-800">
                  {t('onSelectShowtimes', 'On Select Showtimes')}
                </DavidStyledText>
              </SpecificTimesToolTip>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-1 z-10 flex justify-center @sm:hidden">
          <DavidStyledText className="text-[12px]">
            {t('whileSuppliesLastOnePerAdultTicket', 'while supplies last, 1 per adult ticket')}
          </DavidStyledText>
        </div>
      </div>
    </div>
  )
}

export default DavidKidsGoFreeBanner
